import React, {useEffect, useRef, useState} from 'react';

// https://demo.zoomcharts.com/net-chart/
// https://zoomcharts.com/developers/en/net-chart/api-reference/settings/navigation.html
// https://zoomcharts.com/developers/en/full-reference/NetChartSettingsNavigation.html

// context menu opened on right click
function getContextMenuContent(data, item) {
  const is_contact = data.id.startsWith('contact');

  if (is_contact) {
    const content = data.biography ? `<br/><strong>${data.biography}</strong>` : "";
    return `
      <div style="margin:auto; padding: 10px;">
        <strong>${data.firstname} ${data.lastname}</strong>
        ${content}
      </div>
    `
  } else {
    const content = data.description ? `<br/><strong>${data.description}</strong>` : "";
    return `
      <div style="margin:auto; padding: 10px;">
        <strong>${data.name}</strong>
        ${content}
      </div>
    `
  }
}

function initialize(data, {element, height, focus_nodes, onClick, layout, auras}) {
  let hasHovered;

  const opts = focus_nodes ? {
    navigation: {
      mode: "focusnodes",
      // mode: "manual",
      initialNodes: focus_nodes,
      // focusNodeExpansionRadius: 5,
      initialFocusNodeExpansionRadius: 1,
      autoUnfocus: false,
      // numberOfFocusNodes: 2,
      expandOnClick: true
      // defaultExpandDepth: 2,
    }
  } : {
    navigation: {mode: "showall"}
  };

  return new window.NetChart({
    navigation : opts.navigation,
    container: element,
    area: {
      height: height,
      style: { fillColor: "white" }
    },
    data: { preloaded: data },
    layout: layout,
    auras,
    nodeMenu: {
      contentsFunction: getContextMenuContent
    },

    style: {
      node: {
        display: "image",
        imageCropping: true
      },
      nodeDetailMinSize: 0,
      nodeDetailMinZoom: 0,
      // linkDetailMinSize: 100,
      linkDetailMinZoom: 2.2,
      nodeStyleFunction: nodeStyle,
      linkStyleFunction: linkStyle,
      // https://zoomcharts.com/developers/en/net-chart/api-reference/settings/style/linkLabel/textStyle.html
      linkLabel:{
        // borderRadius: 999,  //make as round as possible
        align: "center",
        textStyle:{font:"12px Arial", fillColor: "white"},
        // backgroundStyle:{fillColor:"rgba(86,185,247,0.8)", lineColor:"rgba(28,124,213,0.9)"},
        scaleWithSize: true,
        scaleWithZoom: true, // default true
        aspectRatio: 4, // default 4
      },
      linkLabelScaleBase: 1.3,
    },
    events: {
      onHoverChange: (e) => {
        // hasHovered = !!e.hoverNode;
        // chart.nodes().forEach(node => nodeStyle(node));
      },
      onClick: (e) => {
        if (e.clickNode) {
          const data = e.clickNode.data;
          onClick(data.old_id, data)
        }
      }
    },
    legend: {
      enabled: true,
      padding: 6,
      marker: { size: 22 },
      maxLineSymbols: 12
    }
  });
  // https://zoomcharts.com/developers/en/net-chart/examples/info-popup/custom-popup-on-click.html


  function nodeStyle(node) {
    const {data} = node;

    const is_contact = data.id.startsWith('contact');

    node.aura = data.auras;

    if (is_contact) {
      node.label = data.firstname + " " + data.lastname;
      node.imageCropping = true;
      node.display = 'image';
    } else {
      node.label = data.name;
      node.imageCropping = true;
      node.display = 'rectangle';
    }

    if (data.img) {
      node.image = data.img;
    } else {
      node.fillColor = is_contact ? "lightskyblue" : "lightcoral";
    }

    // https://zoomcharts.com/developers/en/net-chart/introductory-topics/items.html
    // https://zoomcharts.com/developers/en/net-chart/api-reference/settings/style/node/items.html
    if (node.data.trending) {
      node.items = [
        // textStyle: {font: "25px Arial"} radius: "25", scaleWithSize: true
        {px: -1, py: -1, backgroundStyle: {fillColor: "red"}}
      ];
    }

    if (!hasHovered || node.links.some(link => link.from.hovered || link.to.hovered)) {
      // Style applied to hover node & connected nodes.
    } else {
      // Style applied to nodes not hovered or connected to hovered nodes
    }
  }

  function linkStyle(link) {
    link.label = link.data.label;
    // link.fromDecoration = "circle";
    link.toDecoration = "arrow";

    if (link.data.is_old) {
      link.lineDash = 1;
    }

    const color = link.data.link_category.color;

    link.fillColor = color;

    link.labelStyle.backgroundStyle = {
      fillColor: color,
      lineColor: "#333"
    }
  }
}

function buildContactData(contacts, trending_contact, affiliations) {
  let d_trending_contacts = {};
  trending_contact.forEach(x => { d_trending_contacts[x.id] = true; });

  const getAffiliations = (id) => {
    return affiliations.filter((a) => a.contacts.includes(id)).map(a => a.id)
  }

  return contacts
    .map(x => {
    return {...x,
      id: `contact_${x.id}`, type: 'contact', old_id: x.id,
      loaded: true, trending: d_trending_contacts[x.id] || false,
      auras: getAffiliations(x.id)
    };
  })
}

function buildEntityData(entities, trending_entities, affiliations) {
  let d_trending_entities = {};
  trending_entities.forEach(x => { d_trending_entities[x.id] = true; });

  const getAffiliations = (id) => {
    return affiliations.filter((a) => a.entities.includes(id)).map(a => a.id)
  }

  return entities
    .map(x => {
    return {...x,
      id: `entity_${x.id}`, type: 'entity', old_id: x.id,
      loaded: true, trending: d_trending_entities[x.id] || false,
      auras: getAffiliations(x.id)
    };
  })
}

function buildData({ links, contacts, entities, trending, affiliations }, static_mode) {
  // in static mode, hide items that do not have x/y set (rather than setting coordinates to 0)
  if (static_mode) {
    contacts = contacts.filter(item => item.x && item.y);
    entities = entities.filter(item => item.x && item.y);
  }

  const contact_data = buildContactData(contacts, trending.contact, affiliations);
  const entity_data = buildEntityData(entities, trending.entity, affiliations);

  const nodes = contact_data.concat(entity_data);

  const finder = ({ poi_entity_1_id, poi_contact_1_id, poi_entity_2_id, poi_contact_2_id }) => {
    let find_from;
    if (poi_entity_1_id) {
      find_from = entity_data.some(x => x.old_id === poi_entity_1_id);
    } else {
      find_from = contact_data.some(x => x.old_id === poi_contact_1_id);
    }

    if (! find_from) return false;

    if (poi_entity_2_id) {
      return entity_data.some(x => x.old_id === poi_entity_2_id);
    } else {
      return contact_data.some(x => x.old_id === poi_contact_2_id);
    }
  }

  const new_links = links
    .filter(finder)
    .map(l => {

    const from = l.poi_entity_1_id ? `entity_${l.poi_entity_1_id}` : `contact_${l.poi_contact_1_id}`;
    const to = l.poi_entity_2_id ? `entity_${l.poi_entity_2_id}` : `contact_${l.poi_contact_2_id}`;

    return {
      id: l.id,
      to,
      from,
      directed: true,
      label: l.type,
      link_category: l.poi_link_category,
      type: l.type,
      is_old: l.is_old,
    }
  })

  return {
    nodes, links: new_links
  }
}

const onFocusNode = (focusNode, chart, focused, setFocused) => {
  const findNode = chart.nodes().find(n => {
    return n.data.type === focusNode.type && focusNode.id === n.data.old_id
  });
  if(findNode && !focused){
    chart.scrollIntoView([findNode]);
    setFocused(true);
  }
}
// build settings from backoffice params
function buildSettings(settings, affiliations) {
  // https://zoomcharts.com/developers/en/net-chart/api-reference/settings/auras.html
  // https://jsfiddle.net/api/post/jquery/1.12/?example=auras-on-dark-theme.

  let aura_style = {};
  affiliations.forEach(a => {
    aura_style[a.id] = { nameLegend: a.name, fillColor: a.color || "#AAAAAAAA" };
  })

  const auras = settings.auras || {};

  return {
    layout: settings.layout || {mode: "dynamic"},
    auras: {...auras, style: aura_style}
  }
}

function CartoLink({links, contacts, entities, affiliations,  focus_entities, focus_contacts, trending, onClick, map_settings, focusNode, focused, setFocused}) {

  console.log("MAP SETTINGS", map_settings);

  const ref = useRef();
  const [chartAPI, setChartAPI] = useState();
  const settings = buildSettings(map_settings || {}, affiliations);
  const static_mode = settings.layout.mode === "static";

  useEffect(() => {
    const data = buildData({ links, contacts, entities, trending, affiliations }, static_mode);

    // build ids
    const focus_nodes = (focus_entities || []).map(id => "entity_" + id).concat(
      (focus_contacts || []).map(id => "contact_" + id)
    );

    const opts = {
      ...settings,
      element: ref.current,
      height: ref.current.parentNode.offsetHeight,
      focus_nodes: focus_nodes.length ? focus_nodes : null,
      onClick: onClick || (() => {
      })
    };
    if (!chartAPI) {
      setChartAPI(initialize(data, opts));
    }
    if(focusNode !== null && chartAPI){
      onFocusNode(focusNode, chartAPI, focused, setFocused);
    }
  }, [focusNode]); // eslint-disable-line react-hooks/exhaustive-deps


  return <div ref={ref}/>
}


export default CartoLink


